import {
  MetricType,
  MetricWithAttribution,
  onCLS,
  onFCP,
  onINP,
  onLCP,
  onTTFB,
  TTFBAttribution,
} from "web-vitals";

import { RootSpan } from "../types";
import { error, log } from "../utils/logger";

export class WebVitals {
  private currentRootSpan: RootSpan = undefined;
  private clsReported: boolean = false;

  public subscribe(rootSpan: RootSpan) {
    this.currentRootSpan = rootSpan;
    this.clsReported = false;
    log("[L9RUM WebVitals] Subscribing to Web Vitals metrics");
    onCLS(this._setMetricAttributes, { reportAllChanges: false });
    onFCP(this._setMetricAttributes);
    onINP(this._setMetricAttributes, { reportAllChanges: false });
    onLCP(this._setMetricAttributes, { reportAllChanges: false });
    onTTFB(this._setMetricAttributes);
  }

  public unsubscribe() {
    this.currentRootSpan = undefined;
  }

  public isCLSReported(): boolean {
    return this.clsReported;
  }

  private _setMetricAttributes = (metric: MetricType) => {
    if (this.currentRootSpan) {
      const timestamp = Math.floor(Date.now() / 1000);
      const attrs = this._getSpanAttributes(metric, timestamp);
      Object.entries(attrs).forEach(([k, v]) => {
        this.currentRootSpan!.setAttribute(k, v);
      });
      
      // Mark CLS as reported when it's received
      if (metric.name === "CLS") {
        this.clsReported = true;
        log("[L9RUM WebVitals] CLS metric reported and added to span", metric);
      } else {
        log("[L9RUM WebVitals] Metric added to queue", metric);
      }
    } else {
      error("[L9RUM WebVitals] Root span not found");
    }
  };

  private _getSpanAttributes(metric: MetricType | MetricWithAttribution, timestamp: number) {
    const metricName = metric.name.toLowerCase();
    const metricPrefix = `web_vital.${metricName}`;
    const commonAttributes = {
      "web_vital.navigation_type": metric.navigationType,
    };
    const attributes: { [key: string]: string | number } = {
      ...commonAttributes,
      [`${metricPrefix}.id`]: metric.id,
      [`${metricPrefix}.value`]: metric.value,
      [`${metricPrefix}.timestamp`]: timestamp,
      [`${metricPrefix}.rating`]: metric.rating,
      [`${metricPrefix}.delta`]: metric.delta,
    };

    if (metric.entries && metric.entries.length > 0) {
      attributes[`${metricPrefix}.entries_count`] = metric.entries.length;

      if (metric.name === "TTFB" && metric.entries[0]) {
        const entry = metric.entries[0];
        attributes[`${metricPrefix}.start_time`] = entry.startTime;
        attributes[`${metricPrefix}.duration`] = entry.duration;
        attributes[`${metricPrefix}.fetch_start`] = entry.fetchStart;
        attributes[`${metricPrefix}.response_start`] = entry.responseStart;
        attributes[`${metricPrefix}.request_start`] = entry.requestStart;
      }
    }

    const metricWithAttribution = metric as MetricWithAttribution;

    if (metricWithAttribution.attribution) {
      if (metric.name === "TTFB") {
        const attribution = metricWithAttribution.attribution as TTFBAttribution;
        attributes[`${metricPrefix}.waiting_duration`] = attribution.waitingDuration;
        attributes[`${metricPrefix}.dns_duration`] = attribution.dnsDuration;
        attributes[`${metricPrefix}.connection_duration`] = attribution.connectionDuration;
        attributes[`${metricPrefix}.request_duration`] = attribution.requestDuration;
      }
    }

    return attributes;
  }
}
